import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs } from '@trimble-gcs/i18n-types';
import {
  ModusButtonModule,
  ModusIconModule,
  ModusSelectModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { filter, map, Observable, shareReplay, take } from 'rxjs';
import { AppState } from '../../../app-state/app.state';
import { LoadingService } from '../../../loading/loading.service';
import { ImportFile } from '../../import.models';
import { ClarityFileBrowserComponent } from './clarity-file-browser/clarity-file-browser.component';
import { clarityFilePickerTranslations } from './clarity-file-picker.translations';
import { ClarityTreeItem, ClarityUserWorld } from './clarity.models';
import { ClarityService } from './clarity.service';

interface WorldView {
  id: number;
  name: string;
  world: ClarityUserWorld;
}

@Component({
  imports: [
    CommonModule,
    ModusTooltipModule,
    ModusIconModule,
    ModusButtonModule,
    ModusSelectModule,
    MatDialogModule,
    MatProgressBarModule,
    MatIconModule,
    ClarityFileBrowserComponent,
    ReactiveFormsModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './clarity-file-picker.component.html',
  styles: [
    `
      :host {
        height: 100%;
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClarityFilePickerComponent {
  readonly translations = clarityFilePickerTranslations();

  private readonly translationService = inject(TranslationService);
  private readonly loadingService = inject(LoadingService);
  private readonly clarityService = inject(ClarityService);
  private readonly store = inject(Store);

  readonly worldOptions = toSignal(this.getWorldOptions());
  readonly worldControl = new FormControl<WorldView | null>(null);

  private readonly worldControlChanges = toSignal(this.worldControl.valueChanges);

  readonly selectedWorld = computed(() => this.worldControlChanges()?.world);
  readonly selectedItems = signal<ClarityTreeItem[]>([]);
  readonly selectedSummary = computed(() => this.getSelectedSummary());
  readonly selectedSummaryTooltip = computed(() =>
    this.selectedItems()
      .map((item) => item.name)
      .join('\n'),
  );

  readonly isLoading = toSignal(this.loadingService.isLoading$(this));
  readonly disableCancel = signal(false);
  readonly disablePicking = computed(() => this.isLoading() || this.disableCancel());
  readonly disableNext = computed(() => this.disableCancel() || this.selectedItems().length === 0);

  private readonly appSettings = this.store.selectSignal(AppState.settings);
  readonly clarityUrl = computed(() => this.appSettings().clarityUrl);
  readonly showClarityLink = computed(() => this.clarityUrl().length > 0);

  constructor(private dialogRef: MatDialogRef<ClarityFilePickerComponent, ImportFile[]>) {
    this.createWorldOptionsEffect();
    this.selectDefaultWorld();
  }

  setLoading(loading: boolean) {
    if (loading) this.loadingService.startLoading(this);
    else this.loadingService.stopLoading(this);
  }

  selectionChange(changedTreeItems: ClarityTreeItem[]) {
    const selectedItems = this.selectedItems().filter((currentSelectedItem) => {
      // remove items no longer selected
      const changedItem = changedTreeItems.find((item) => item.id === currentSelectedItem.id);
      return isNil(changedItem) ? true : changedItem.selected;
    });

    const addItems = changedTreeItems.filter(
      (changedItem) =>
        changedItem.selected && isNil(selectedItems.find((item) => item.id === changedItem.id)),
    );

    const newSelectedItems = [...selectedItems, ...addItems];
    this.selectedItems.set(newSelectedItems);
  }

  cancelClick() {
    this.dialogRef.close();
  }

  nextClick() {
    this.loadingService.startLoading(this);
    this.disableCancel.set(true);
    this.worldControl.disable();

    this.clarityService
      .mapToImportFiles(this.selectedItems())
      .subscribe((importFiles) => this.dialogRef.close(importFiles));
  }

  private createWorldOptionsEffect() {
    effect(() => {
      if (isNil(this.worldOptions())) {
        this.worldControl.disable();
      } else {
        this.worldControl.enable();
      }
    });
  }

  private selectDefaultWorld() {
    // set initial world value
    toObservable(this.worldOptions)
      .pipe(
        filter((worlds) => isDefined(worlds)),
        take(1),
      )
      .subscribe((worlds) => this.worldControl.setValue(worlds.at(0) ?? null));
  }

  private getWorldOptions(): Observable<WorldView[]> {
    const getWorlds$ = this.clarityService.getWorlds().pipe(
      map((worlds) => {
        return worlds.map((world) => {
          const name = isDefined(world.entitlement.accountName)
            ? `${world.name} (${world.entitlement.accountName})`
            : world.name;

          return {
            id: world.id,
            name,
            world,
          } satisfies WorldView;
        });
      }),
      map((worldViews) => worldViews.toSorted((a, b) => a.name.localeCompare(b.name))),
    );

    return this.loadingService
      .loadFrom(getWorlds$, this)
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));
  }

  private getSelectedSummary() {
    const quantity = this.selectedItems().length;
    if (quantity === 0) return;

    const pluralArgs: PluralArgs = { count: quantity };
    const pluralMap = this.translations.selectedQty;
    return this.translationService.translatePlural(pluralArgs, pluralMap);
  }
}
